import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowLeft, Trophy, Building2 } from "lucide-react";

const RANK_STYLE = {
  1: "bg-amber-50 border-amber-200 text-amber-700",
  2: "bg-slate-100 border-slate-300 text-slate-600",
  3: "bg-orange-50 border-orange-200 text-orange-700",
};

function RankBadge({ n }) {
  const style = RANK_STYLE[n] || "bg-white border-slate-200 text-slate-500";
  return (
    <div className={cn("h-9 w-9 shrink-0 rounded-full border flex items-center justify-center font-mono text-sm font-semibold", style)}>
      {n}
    </div>
  );
}

function Pct({ value }) {
  const pct = Math.round((value || 0) * 100);
  const tone = pct >= 90 ? "text-emerald-600" : pct >= 70 ? "text-slate-700" : "text-orange-600";
  return <span className={cn("font-mono font-medium", tone)}>{pct}%</span>;
}

/**
 * Org-level marketplace leaderboard — deliberately its OWN page/component,
 * never sharing markup with the employee Leaderboard.js, so personal
 * reputation and org marketplace rank stay visually and structurally
 * distinct (PRD gap #18).
 */
export default function MarketplaceLeaderboard() {
  const { org } = useAuth();
  const navigate = useNavigate();
  const [category, setCategory] = useState("overall");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/marketplace/leaderboard", { params: category === "overall" ? {} : { category } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  const tabs = ["overall", ...(data?.categories || [])];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-blue-600">Marketplace</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2 flex items-center gap-3">
          <Trophy className="h-8 w-8 text-amber-500" /> Org Leaderboard
        </h1>
        <p className="text-slate-600 mt-1 max-w-xl">
          Ranked by delivery track record on the marketplace — completion rate, on-time
          delivery, and dispute history. Overall combines every category an org has
          completed work in.
        </p>
      </div>

      {!loading && data?.my_rank && (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-white">
          <CardContent className="p-5 flex items-center gap-4">
            <RankBadge n={data.my_rank} />
            <div className="flex-1">
              <div className="text-sm text-slate-500">Your org's rank {category !== "overall" ? `in ${category}` : "(overall)"}</div>
              <div className="font-display text-xl font-bold tracking-tight">#{data.my_rank} of {data.total_ranked}</div>
            </div>
            <Link to={`/app/marketplace/orgs/${org?.id}`}>
              <Button variant="outline" size="sm">View our profile</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {tabs.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            data-testid={`leaderboard-tab-${c}`}
            className={cn(
              "px-4 py-1.5 rounded-full text-sm font-medium transition-colors",
              category === c ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            )}
          >
            {c === "overall" ? "Overall" : c}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : !data?.board?.length ? (
        <p className="text-slate-500 text-sm">No completed tenders in this category yet.</p>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
          {data.board.map((row) => {
            const mine = row.org_id === data.my_org_id;
            return (
              <Link
                key={row.org_id}
                to={`/app/marketplace/orgs/${row.org_id}`}
                data-testid={`leaderboard-row-${row.org_id}`}
                className={cn(
                  "flex items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-50",
                  mine && "bg-blue-50/70 hover:bg-blue-50 ring-1 ring-inset ring-blue-200"
                )}
              >
                <RankBadge n={row.rank_position} />
                <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <Building2 className="h-4 w-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                    {row.org_name}
                    {mine && <Badge className="bg-blue-600 hover:bg-blue-600">You</Badge>}
                  </div>
                  <div className="text-xs text-slate-500">
                    completion <Pct value={row.completion_rate} /> · on-time <Pct value={row.on_time_rate} />
                    {row.dispute_rate > 0 && (
                      <span className="text-orange-600"> · disputes {Math.round(row.dispute_rate * 100)}%</span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-lg font-semibold text-slate-900">{row.rank_score}</div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">rank score</div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
